import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import Loading from '@/components/Loading';
import WxAuthGuard from '@/router/wxAuth/WxAuthGuard';
import RouteCacheLayout from '@/router/RouteCacheLayout';

// 懒加载页面组件
const Auth = lazy(() => import('@/pages/auth'));
const Coupon = lazy(() => import('@/pages/coupon'));
// const Scene = lazy(() => import('@/pages/scene'));
const SceneMain = lazy(() => import('@/pages/scene/main'));
const Profile = lazy(() => import('@/pages/profile'));
const ProductDetail = lazy(() => import('@/pages/product-detail'));
const NotFound = lazy(() => import('@/pages/not-found'));
const NotPermission = lazy(() => import('@/pages/not-permission'));
const OrderList = lazy(() => import('@/pages/order-list'));
const OrderDetails = lazy(() => import('@/pages/order-details'));
const MemberOrderDetail = lazy(() => import('@/pages/member-order-detail'));
const OrderRefund = lazy(() => import('@/pages/order-refund'));
const RefundDetail = lazy(() => import('@/pages/refund-detail'));
const TourismRefundDetail = lazy(() => import('@/pages/refund-detail/tourism-page'));
const OrderConfirmNormal = lazy(() => import('@/pages/order-confirm/normal-page'));
const OrderConfirmTourism = lazy(() => import('@/pages/order-confirm/tourism-page'));
const OrderConfirmMember = lazy(() => import('@/pages/order-confirm/member-page'));
const OrderConfirmPoints = lazy(() => import('@/pages/order-confirm/points-page'));
const PointsMall = lazy(() => import('@/pages/points-mall'));
const PointsDetails = lazy(() => import('@/pages/points-detail'));
const Address = lazy(() => import('@/pages/address'));
const MemberCenter = lazy(() => import('@/pages/member-center'));
const ServiceCenter = lazy(() => import('@/pages/service-center'));
const ShoppingFestival = lazy(() => import('@/pages/shopping-festival'));
const PaymentResult = lazy(() => import('@/pages/payment-result'));
const PaymentResultTourism = lazy(() => import('@/pages/payment-result/tourism-page'));
const TourismOrderDetails = lazy(() => import('@/pages/order-details/tourism-page'));
const TradeSnapshot = lazy(() => import('@/pages/trade-snapshot'));
const TourismPriceDiff = lazy(() => import('@/pages/tourism-price-diff'));
const AppLink = lazy(() => import('@/pages/applink'));
const ShareBridge = lazy(() => import('@/pages/share-bridge'));
const DownloadGuide = lazy(() => import('@/pages/download-guide'));
const DownloadGuide2 = lazy(() => import('@/pages/download-guide-2'));
const DownloadPandora = lazy(() => import('@/pages/download-pandora'));
const WechatPage = lazy(() => import('@/pages/wechat-page'));
const PrivacyPolicy = lazy(() => import('@/pages/privacy-policy'));
const PersonalInfoCollectionList = lazy(() => import('@/pages/personal-info-collection-list'));
const ThirdPartySharingList = lazy(() => import('@/pages/third-party-sharing-list'));
const BlackDiamondMemberAgreement = lazy(() => import('@/pages/black-diamond-member-agreement'));
const NewMemberCenter = lazy(() => import('@/pages/new-member-center'));
const WriteOffDetail = lazy(() => import('@/pages/writeoff-detail'));

// 懒加载包装组件
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

// 路由配置
export const router = createBrowserRouter(
  [
    // 404 页面不需要微信授权
    {
      path: '/404',
      element: (
        <LazyWrapper>
          <NotFound />
        </LazyWrapper>
      ),
    },
    {
      path: '/not-permission/:code',
      element: (
        <LazyWrapper>
          <NotPermission />
        </LazyWrapper>
      ),
    },
    {
      path: '/auth',
      element: (
        <LazyWrapper>
          <Auth />
        </LazyWrapper>
      ),
    },
    {
      path: '/applink/share',
      element: (
        <LazyWrapper>
          <AppLink />
        </LazyWrapper>
      ),
    },
    {
      path: '/applink/share-bridge',
      element: (
        <LazyWrapper>
          <ShareBridge />
        </LazyWrapper>
      ),
    },
    {
      path: '/download-guide',
      element: (
        <LazyWrapper>
          <DownloadGuide />
        </LazyWrapper>
      ),
    },
    {
      path: '/download-guide-2',
      element: (
        <LazyWrapper>
          <DownloadGuide2 />
        </LazyWrapper>
      ),
    },
    {
      path: '/download-pandora',
      element: (
        <LazyWrapper>
          <DownloadPandora />
        </LazyWrapper>
      ),
    },
    {
      path: '/wechat-page',
      element: (
        <LazyWrapper>
          <WechatPage />
        </LazyWrapper>
      ),
    },
    {
      path: '/privacy-policy',
      element: (
        <LazyWrapper>
          <PrivacyPolicy />
        </LazyWrapper>
      ),
    },
    {
      path: '/personal-info-collection-list',
      element: (
        <LazyWrapper>
          <PersonalInfoCollectionList />
        </LazyWrapper>
      ),
    },
    {
      path: '/third-party-sharing-list',
      element: (
        <LazyWrapper>
          <ThirdPartySharingList />
        </LazyWrapper>
      ),
    },
    {
      path: '/black-diamond-member-agreement',
      element: (
        <LazyWrapper>
          <BlackDiamondMemberAgreement />
        </LazyWrapper>
      ),
    },
    // 需要微信授权的页面
    {
      element: <WxAuthGuard />,
      children: [
        {
          element: <RouteCacheLayout />,
          children: [
            {
              path: '/scene',
              element: (
                <LazyWrapper>
                  <SceneMain />
                </LazyWrapper>
              ),
            },
            {
              path: '/scene-main',
              element: (
                <LazyWrapper>
                  <SceneMain />
                </LazyWrapper>
              ),
            },
            {
              path: '/profile',
              element: (
                <LazyWrapper>
                  <Profile />
                </LazyWrapper>
              ),
            },
            {
              path: '/product-detail',
              element: (
                <LazyWrapper>
                  <ProductDetail />
                </LazyWrapper>
              ),
            },
            {
              path: '/member-center',
              element: (
                <LazyWrapper>
                  <MemberCenter />
                </LazyWrapper>
              ),
            },
            {
              path: '/new-member-center',
              element: (
                <LazyWrapper>
                  <NewMemberCenter />
                </LazyWrapper>
              ),
            },
            {
              path: '/service-center',
              element: (
                <LazyWrapper>
                  <ServiceCenter />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-list',
              element: (
                <LazyWrapper>
                  <OrderList />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-details',
              element: (
                <LazyWrapper>
                  <OrderDetails />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-details/tourism',
              element: (
                <LazyWrapper>
                  <TourismOrderDetails />
                </LazyWrapper>
              ),
            },
            {
              path: '/member-order-detail',
              element: (
                <LazyWrapper>
                  <MemberOrderDetail />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-confirm',
              element: (
                <LazyWrapper>
                  <OrderConfirmNormal />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-confirm/tourism',
              element: (
                <LazyWrapper>
                  <OrderConfirmTourism />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-confirm/member',
              element: (
                <LazyWrapper>
                  <OrderConfirmMember />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-confirm/points',
              element: (
                <LazyWrapper>
                  <OrderConfirmPoints />
                </LazyWrapper>
              ),
            },
            {
              path: '/order-refund',
              element: (
                <LazyWrapper>
                  <OrderRefund />
                </LazyWrapper>
              ),
            },
            {
              path: '/refund-detail',
              element: (
                <LazyWrapper>
                  <RefundDetail />
                </LazyWrapper>
              ),
            },
            {
              path: '/refund-detail/tourism',
              element: (
                <LazyWrapper>
                  <TourismRefundDetail />
                </LazyWrapper>
              ),
            },
            {
              path: '/points-detail',
              element: (
                <LazyWrapper>
                  <PointsDetails />
                </LazyWrapper>
              ),
            },
            {
              path: '/coupon',
              element: (
                <LazyWrapper>
                  <Coupon />
                </LazyWrapper>
              ),
            },
            {
              path: '/points-mall',
              element: (
                <LazyWrapper>
                  <PointsMall />
                </LazyWrapper>
              ),
            },
            {
              path: '/address',
              element: (
                <LazyWrapper>
                  <Address />
                </LazyWrapper>
              ),
            },
            {
              path: '/shopping-festival',
              element: (
                <LazyWrapper>
                  <ShoppingFestival />
                </LazyWrapper>
              ),
            },
            {
              path: '/payment-result',
              element: (
                <LazyWrapper>
                  <PaymentResult />
                </LazyWrapper>
              ),
            },
            {
              path: '/payment-result/tourism',
              element: (
                <LazyWrapper>
                  <PaymentResultTourism />
                </LazyWrapper>
              ),
            },
            {
              path: '/trade-snapshot',
              element: (
                <LazyWrapper>
                  <TradeSnapshot />
                </LazyWrapper>
              ),
            },
            {
              path: '/tourism-price-diff',
              element: (
                <LazyWrapper>
                  <TourismPriceDiff />
                </LazyWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      path: '/writeoff-detail',
      element: (
        <LazyWrapper>
          <WriteOffDetail />
        </LazyWrapper>
      ),
    },
    {
      path: '*',
      element: <Navigate to='/404' replace />,
    },
  ],
  {
    basename: '/',
  },
);

export default router;
